import { useMutation, useQueryClient } from 'react-query'
import { AlmanacFileCalendarItem } from './types'
import { calendarKeys } from './calendarKeys'
import { sendToApi } from 'api/client'
import {
  handleMutationError,
  processDateRangeQueries,
  setupMutation,
  updateItemProcessor,
  updateUnscheduledItems,
} from './utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

export function useUpdateCalendarItem(projectKey: string) {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const filters = { projectKeys: [projectKey] }
  return useMutation(
    ({ updatedItem }: { updatedItem: AlmanacFileCalendarItem }) => {
      return sendToApi(`/w/${workspaceId}/calendar_items/${updatedItem.key}`, {
        method: 'PUT',
        body: { calendar_item: updatedItem },
      })
    },
    {
      onMutate: async ({
        updatedItem,
        originalPostAt,
      }: {
        updatedItem: AlmanacFileCalendarItem
        originalPostAt: string | null
      }) => {
        const context = await setupMutation(queryClient, filters)

        processDateRangeQueries(
          queryClient,
          filters,
          updateItemProcessor(updatedItem, originalPostAt),
          context.previousData,
        )

        const wasUnscheduled = !originalPostAt
        const isUnscheduled = !updatedItem.post_at

        if (wasUnscheduled !== isUnscheduled) {
          updateUnscheduledItems(
            queryClient,
            filters,
            isUnscheduled ? 'add' : 'remove',
            updatedItem,
            context.previousData,
          )
        }

        return context
      },
      onError: (error, variables, context) => handleMutationError(queryClient, error, variables, context),
      onSettled: () => queryClient.invalidateQueries(calendarKeys.filters(filters)),
    },
  )
}
